import { useCallback, useLayoutEffect, useState } from 'react'

function checkOverflow(element: HTMLElement): boolean {
  return element.scrollHeight > element.clientHeight
}

/**
 * Allows monitoring whether an element is overflowing its container (with the presence of a scrollbar on desktop)
 * Use the returned `ref` on the element to measure (ref identity stays stable so the effect runs when the node is attached).
 * @returns `{ isOverflowing, ref }` — attach `ref` to the element you want to observe
 */
export function useIsOverflowing(): {
  isOverflowing: boolean
  ref: (instance: HTMLElement | null) => void
} {
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [element, setElement] = useState<HTMLElement | null>(null)

  /**
   * Avoid getting the first ref as null when hook run before DOM element is mounted,
   * making the behaviour unconsistent.
   */
  const ref = useCallback((instance: HTMLElement | null) => {
    setElement(instance)
  }, [])

  useLayoutEffect(() => {
    if (!element) return

    const updateOverflow = () => {
      setIsOverflowing(checkOverflow(element))
    }

    // initial check
    updateOverflow()

    // Once the element reaches its maximum height it stops resizing, so content that grows afterwards (an image
    // decoding, a web font swapping in) only changes its scrollHeight. Observing the content too catches that growth.
    const resizeObserver = new ResizeObserver(updateOverflow)
    const observeSubtree = (root: Element) => {
      resizeObserver.observe(root)
      for (const descendant of root.querySelectorAll('*')) resizeObserver.observe(descendant)
    }
    const unobserveSubtree = (root: Element) => {
      resizeObserver.unobserve(root)
      for (const descendant of root.querySelectorAll('*')) resizeObserver.unobserve(descendant)
    }
    observeSubtree(element)

    const mutationObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) if (node instanceof Element) observeSubtree(node)
        for (const node of record.removedNodes) if (node instanceof Element) unobserveSubtree(node)
      }
      updateOverflow()
    })
    mutationObserver.observe(element, { childList: true, subtree: true })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [element])

  return { isOverflowing, ref }
}
